/**
 * The workspace domain's FLAG MODULE (ruling 36 as amended 2026-08-29; converted
 * in flags fan-out batch 2).
 *
 * ONE DOOR, ONE FILE. The `defineFeatureFlag` descriptors and the named getters
 * that read them are CO-LOCATED here, and `@heidi/workspace/flags` publishes
 * both. `/contributions` carries nothing for flags — this domain has no such
 * door at all, and does not need one to declare a flag.
 *
 * ONE MINTING SITE PER FLAG, made structural rather than conventional by
 * co-location: each getter reads `descriptor.key` / `descriptor.defaultValue`
 * from the declaration directly above it, so the pair cannot drift, and
 * `flags.test.ts` asserts each key appears exactly once in this file and that no
 * read takes a string literal — the only assertion that can tell a descriptor
 * read from a hardcoded string that happens to be equal today. Flag keys have no
 * cross-site-identity guard (cache keys do), so a second spelling would be
 * invisible to every other gate in the repo. That matters more here than in
 * either batch sibling: fourteen keys, of which
 * `release-org-direct-session-sharing-setting` gates one half of the org
 * collaboration settings screen while the override is now unconditional,
 * and `release-workspace-switcher` and
 * `release-workspace-settings` are the two operands of one combined gate.
 *
 * THE DOOR IS HEADLESS, guarded transitively rather than by inspection.
 * `scripts/monorepo-migration/lib/package-ui-zone.ts` admits
 * `@heidi/runtime/feature-flags` ONLY from this file out of everything under
 * `packages/workspace/src`; `scripts/check-flag-getter-doors.test.ts` walks this
 * module's graph ACROSS package boundaries and refuses `@heidi/design-system`,
 * this package's own `/ui` zone and the app tree anywhere in it, and separately
 * proves the door imports in a bare Node process with no DOM and no side
 * effects.
 *
 * PACKAGE UI IMPORTS THESE RELATIVELY (`../flags`), never through the door — a
 * package that imports itself by name resolves through node_modules and reads as
 * an undeclared external dependency. The app barrel `@/features/flags`
 * re-exports the getters from `@heidi/workspace/flags`, so its importers and
 * their `jest.mock` literals are untouched.
 *
 * WORKSPACE IS THE FOUNDATIONAL DOMAIN (§5.3), which makes one thing below
 * unusually easy to get wrong and worth stating: every other domain may reach
 * this package at any door with no declaration, so the moment a foreign domain's
 * surface wants one of these booleans it can simply import the getter. It must
 * not. Ruling 36 (b) is about the DEPENDENCY DIRECTION of a flag read, and the
 * foundational exemption does not license a foreign domain to bind this door for
 * one boolean — the resolved value crosses as a prop at the composition point
 * that already owns it. The dictation roots reading `useIsWorkspaceSettingsIaEnabled`
 * are the live example, and they take it as a prop today.
 *
 * WHAT DOES NOT BELONG HERE — three of this domain's eleven app-barrel getters
 * are NOT below, and each is named rather than silently skipped. All three are
 * mechanism. (`useIsTeamsTableRedesignEnabled` was a fourth until its flag moved
 * from PostHog to Flagsmith, which is what made it door-readable; it now sits
 * below with the rest.)
 *
 *   - `useIsDictationLanguageSettingsFlagEnabled` and
 *     `useIsDictationOutputLanguageFlagEnabled` AND their flag with
 *     `useIsPlatformDictationEnabled()`, which resolves through PostHog. The
 *     composition inherits the mechanism, so the pair is not door-readable
 *     however plain the flag half looks — verbatim the case
 *     `packages/dictate/src/flags.ts` records as the reason its own siblings
 *     stayed.
 *   - `useIsDictationLogOptOutEnabled` is a bare ALIAS of that PostHog gate and
 *     reads no flag of its own. It is also dictation-owned by noun; it belongs
 *     to that lane, not this one.
 *
 * All three move when their mechanism does, not before, and each carries its
 * classification at its app-barrel site as well as here.
 */
import { defineFeatureFlag, useBooleanFlag } from '@heidi/runtime/feature-flags';

// ---------------------------------------------------------------------------
// The declarations. `key` is a FROZEN EXTERNAL IDENTITY — Flagsmith targets the
// flag by it, so renaming one is switching flags, never a refactor.
//
// `builds: 'all'` on the twelve below `multiWorkspaceMembershipFlag`, measured
// rather than assumed; that next descriptor is `'cloud-only'` and says why. The surfaces are
// the organisation and team settings screens, the onboarding/account
// organisation-name field, the organisation and team members tables, the
// sidebar workspace switcher and the account login-details card, and not one of
// them carries an `isOnPrem` gate: `SideNav` has such gates and the switcher
// sits OUTSIDE them, `AccountSettings` renders `LoginDetails` unconditionally,
// the settings list is flag-gated rather than build-gated, and the team-table
// redesign swaps a component identity while `teamForm`'s `isOnPrem` gates guard
// billing, not the table. Omitting the field would silently take the D6 default
// (`'cloud-only'`) and withhold from the appliance twelve surfaces it already
// ships. Contrast `@heidi/calls`, which omits it on the opposite finding: its
// telephony path refuses the airgapped build in its own code.
// ---------------------------------------------------------------------------

/**
 * MUL-826: the org-level toggle that enables or disables direct session
 * sharing.
 */
export const orgDirectSessionSharingSettingFlag = defineFeatureFlag({
  key: 'release-org-direct-session-sharing-setting',
  defaultValue: false,
  description: 'Org-level direct session-sharing toggle',
  builds: 'all',
});

/**
 * The "Workspace name" repurposing of the onboarding / setup / account
 * "Organisation name" field — the relabelled copy, the new placeholder and the
 * `<first name>'s Workspace` prefill. OFF renders the legacy "Organisation name"
 * experience everywhere.
 */
export const workspaceOnboardingRenameFlag = defineFeatureFlag({
  key: 'release-workspace-onboarding-rename',
  defaultValue: false,
  description: 'Workspace-name repurposing of the organisation-name field',
  builds: 'all',
});

/**
 * MUL-1735: org-admin bulk and per-row "log out everywhere" on the organisation
 * members table. Default OFF so a destructive action can roll out gradually and
 * be killed instantly; it gates the selection UI and the per-row item on top of
 * the existing admin check rather than replacing it.
 */
export const orgAdminBulkLogoutFlag = defineFeatureFlag({
  key: 'release-org-admin-bulk-logout',
  defaultValue: false,
  description: 'Org-admin bulk and per-row log-out-everywhere',
  builds: 'all',
});

/**
 * The sidebar workspace switcher menu and the wider M8 UI series. The single
 * point every M8 surface checks, so the menu renders only for rolled-out
 * cohorts.
 */
export const workspaceSwitcherFlag = defineFeatureFlag({
  key: 'release-workspace-switcher',
  defaultValue: false,
  description: 'Sidebar workspace switcher and the M8 UI series',
  builds: 'all',
});

/** The Account settings "Login with Heidi App" setup row. */
export const loginWithHeidiAppFlag = defineFeatureFlag({
  key: 'release-login-with-heidi-app',
  defaultValue: false,
  description: 'Account settings "Login with Heidi App" setup row',
  builds: 'all',
});

/**
 * The raw `release-workspace-settings` flag.
 *
 * ZERO DIRECT PRODUCTION CONSUMERS at conversion, and that is recorded rather
 * than acted on — but it is emphatically NOT dead. Its one reader is the app
 * barrel's `useIsWorkspaceSettingsIaEnabled`, the combined settings-IA gate that
 * ANDs it with the workspace switcher above, and sixteen production files read
 * THAT. A getter nobody reads directly would be a candidate for retirement
 * anyway; retiring a flag is a `docs/feature-flag-cleanups.md` change with a
 * provider step behind it, which is a different decision from where the
 * declaration lives. So it moves with its siblings and is not deleted.
 *
 * Prefer the combined gate for settings-IA surfaces. Reach for this one only
 * when you need the raw value — composing the combined gate itself is the case
 * that exists.
 */
export const workspaceSettingsFlag = defineFeatureFlag({
  key: 'release-workspace-settings',
  defaultValue: false,
  description: 'Raw workspace-settings flag behind the settings-IA combined gate',
  builds: 'all',
});

/**
 * The redesigned team and organisation members data tables — the shared
 * design-system `DataTable` replacing the bespoke `TeamTableV2` and
 * `OrganizationMembersTable`. Covers both surfaces so they roll out together;
 * flipped per-environment in Flagsmith. Was a PostHog read app-side, which is
 * the only reason it did not move with its siblings; the provider migration is
 * what made it door-readable, its twin being `release-templates-table-redesign`.
 */
export const teamsTableRedesignFlag = defineFeatureFlag({
  key: 'release-teams-table-redesign',
  defaultValue: false,
  description: 'The redesigned team and organisation members data tables',
  builds: 'all',
});

/**
 * The shared patient directory (MUL-3181 series). The SAME Flagsmith flag the
 * ml-scribe backend gates the `workspace-share_patients_enabled` consent write
 * on, so the settings section and the write it drives cannot drift apart.
 */
export const sharedPatientDirectoryFlag = defineFeatureFlag({
  key: 'release-scribe-shared-patient-directory',
  defaultValue: false,
  description: 'Workspace settings shared-patient-directory opt-in section',
  builds: 'all',
});

/**
 * Pre-action warning that a sensitive account change signs the user out of
 * every device. Stays in lock-step with the backend revocation flag.
 */
export const logoutEverywhereOnSensitiveActionsFlag = defineFeatureFlag({
  key: 'release-ml-scribe-logout-everywhere-on-sensitive-actions',
  defaultValue: false,
  description: 'Warn before sensitive actions that sign the user out everywhere',
  builds: 'all',
});

/**
 * Kill switch for the org custom-consent textarea. Ships ON until Flagsmith
 * pulls it. The require-patient-consent toggle stays ungated.
 */
export const orgCustomConsentTextFlag = defineFeatureFlag({
  key: 'release-custom-consent-text',
  defaultValue: true,
  description: 'Org custom consent message textarea',
  builds: 'all',
});

/** Transcript retention row on the team data-management settings. */
export const teamTranscriptRetentionFlag = defineFeatureFlag({
  key: 'release-scribe-transcript-retention-team',
  defaultValue: false,
  description: 'Team transcript retention settings row',
  builds: 'all',
});

/** Transcript retention row on the organisation data-management settings. */
export const orgTranscriptRetentionFlag = defineFeatureFlag({
  key: 'release-scribe-transcript-retention-org',
  defaultValue: false,
  description: 'Organisation transcript retention settings row',
  builds: 'all',
});

/**
 * Multi-workspace membership: the post-login workspace selector a
 * user who belongs to 2+ workspaces picks from before the app mounts. The SAME
 * Flagsmith flag the ml-scribe backend reads for its multi-workspace paths, so
 * the selector and the backend behaviour it assumes roll out together.
 *
 * `builds: 'cloud-only'` — the ONE exception to the `'all'` above, and measured
 * the same way: the selector renders from `WorkspaceHydrationGate`, which the
 * on-prem Keycloak branch of `AuthWrapper` never mounts, so the appliance has
 * no surface for this flag to gate. Omitting the field takes the D6 default,
 * which is exactly that; it is spelled out so the difference from its siblings
 * reads as a decision rather than an oversight.
 *
 * Evaluated BEFORE the user identity reaches the flag provider (the gate sits
 * above the context sync), so it must be rolled out per environment, never by
 * segment or percentage.
 */
export const multiWorkspaceMembershipFlag = defineFeatureFlag({
  key: 'release-multiplayer-enable-multi-workspace-membership',
  defaultValue: false,
  description: 'Multi-workspace membership: the post-login workspace selector',
  builds: 'cloud-only',
});

/** Cloud-only by default: its only host is the cloud-only global modals mount. */
export const pendingTeamInvitePromptFlag = defineFeatureFlag({
  key: 'release-multiplayer-pending-team-invite-prompt',
  defaultValue: false,
  description: 'Post-signup prompt for pending team invitations',
});

/**
 * Settings -> Workspace -> General. ml-scribe reads the same key to 404 the writes
 * while off; `builds` stays cloud-only until on-prem icon storage is verified.
 */
export const workspaceGeneralSettingsFlag = defineFeatureFlag({
  key: 'release-workspace-general-settings',
  defaultValue: false,
  description: 'Settings -> Workspace -> General page (workspace name + avatar)',
});

// ---------------------------------------------------------------------------
// The getters. Each reads the pair off the descriptor above it.
//
// ONE DISCLOSED BEHAVIOURAL DELTA. `useBooleanFlag` honours the injected
// `forceEnableAll` override. The original eight bodies had no hand-rolled
// `process.env` branch, so they GAIN the dev-only knob. Of the four added in
// the top-up, logout-everywhere and the org custom-consent kill switch gain it
// the same way; the team and org transcript-retention getters COLLAPSE the
// hand-rolled `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS` branch they carried
// onto that same override. The knob is dev-only (design D8).
// ---------------------------------------------------------------------------

/** The org-level direct session-sharing toggle (MUL-826). */
export const useIsOrgDirectSessionSharingSettingEnabled = (): boolean =>
  useBooleanFlag(
    orgDirectSessionSharingSettingFlag.key,
    orgDirectSessionSharingSettingFlag.defaultValue
  );

/** The workspace-name repurposing of the organisation-name field. */
export const useIsWorkspaceOnboardingRenameEnabled = (): boolean =>
  useBooleanFlag(
    workspaceOnboardingRenameFlag.key,
    workspaceOnboardingRenameFlag.defaultValue
  );

/** Org-admin bulk and per-row log-out-everywhere (MUL-1735). */
export const useIsOrgAdminBulkLogoutEnabled = (): boolean =>
  useBooleanFlag(orgAdminBulkLogoutFlag.key, orgAdminBulkLogoutFlag.defaultValue);

/** The sidebar workspace switcher and the M8 UI series. */
export const useIsWorkspaceSwitcherEnabled = (): boolean =>
  useBooleanFlag(workspaceSwitcherFlag.key, workspaceSwitcherFlag.defaultValue);

/** The Account settings "Login with Heidi App" setup row. */
export const useIsLoginWithHeidiAppEnabled = (): boolean =>
  useBooleanFlag(loginWithHeidiAppFlag.key, loginWithHeidiAppFlag.defaultValue);

/**
 * The raw workspace-settings flag.
 *
 * The COMBINED settings-IA gate that ANDs this with the switcher stays in the
 * app barrel and is not below, which is a deliberate reading of §3 rather than
 * an oversight: `useIsWorkspaceSettingsIaEnabled` declares no key of its own and
 * its body reads no flag — it composes two getters. Composition of a domain's
 * OWN getters may travel into the module, so moving it would be legal; it is
 * simply not this batch's, whose unit is the twelve owned bodies that read a
 * flag and nothing else. The barrel keeps composing it out of these two
 * re-exports and every one of its sixteen consumers is untouched either way.
 */
export const useIsWorkspaceSettingsEnabled = (): boolean =>
  useBooleanFlag(workspaceSettingsFlag.key, workspaceSettingsFlag.defaultValue);

/** The redesigned team and organisation members data tables. */
export const useIsTeamsTableRedesignEnabled = (): boolean =>
  useBooleanFlag(teamsTableRedesignFlag.key, teamsTableRedesignFlag.defaultValue);

/** The workspace-settings shared-patient-directory opt-in section. */
export const useIsSharedPatientDirectoryEnabled = (): boolean =>
  useBooleanFlag(sharedPatientDirectoryFlag.key, sharedPatientDirectoryFlag.defaultValue);

/** Warn before sensitive account changes that sign the user out everywhere. */
export const useIsLogoutEverywhereOnSensitiveActionsEnabled = (): boolean =>
  useBooleanFlag(
    logoutEverywhereOnSensitiveActionsFlag.key,
    logoutEverywhereOnSensitiveActionsFlag.defaultValue
  );

/** Org custom-consent textarea. Ships ON; Flagsmith can pull it. */
export const useIsOrgCustomConsentTextEnabled = (): boolean =>
  useBooleanFlag(orgCustomConsentTextFlag.key, orgCustomConsentTextFlag.defaultValue);

/** Team data-management transcript retention row. */
export const useIsTeamTranscriptRetentionEnabled = (): boolean =>
  useBooleanFlag(
    teamTranscriptRetentionFlag.key,
    teamTranscriptRetentionFlag.defaultValue
  );

/** Organisation data-management transcript retention row. */
export const useIsOrgTranscriptRetentionEnabled = (): boolean =>
  useBooleanFlag(orgTranscriptRetentionFlag.key, orgTranscriptRetentionFlag.defaultValue);

/** Multi-workspace membership: the post-login workspace selector. */
export const useIsMultiWorkspaceMembershipEnabled = (): boolean =>
  useBooleanFlag(
    multiWorkspaceMembershipFlag.key,
    multiWorkspaceMembershipFlag.defaultValue
  );

/** The post-signup prompt for pending team invitations. */
export const useIsPendingTeamInvitePromptEnabled = (): boolean =>
  useBooleanFlag(
    pendingTeamInvitePromptFlag.key,
    pendingTeamInvitePromptFlag.defaultValue
  );

/** Settings -> Workspace -> General page (workspace name + avatar). */
export const useIsWorkspaceGeneralSettingsEnabled = (): boolean =>
  useBooleanFlag(
    workspaceGeneralSettingsFlag.key,
    workspaceGeneralSettingsFlag.defaultValue
  );

/**
 * What the app's `useMultiWorkspaceSelectionGate` returns — the ONE reading of
 * `multiWorkspaceMembershipFlag` above, so the boot writer
 * (`useHydrateWorkspaceId`, above the hydration gate) and
 * `useWorkspaceHydrationStatus` can never disagree about whether the default
 * pick is deferred.
 *
 * It is declared HERE rather than beside the seam it feeds
 * (`WorkspaceDataConfig` in `./data/config`, which re-exports it so the `/data`
 * surface is unchanged) because the app flags barrel names this type, and that
 * barrel may bind a domain package through `./flags` and nothing else —
 * `src/features/flags/domain-flag-doors.test.ts` scans specifiers textually, so
 * an erased `import type` off `/data` fails it exactly like a value import.
 *
 * A type is not a getter, so it does not widen this door's runtime surface:
 * both the shape rule in `scripts/check-flag-getter-doors.test.ts` and the
 * exact-set fixture in `./flags.test.ts` read the door's runtime keys.
 */
export type MultiWorkspaceSelectionGate = {
  /** The flag has settled ON: a 2+ workspace user picks instead of being auto-assigned. */
  isSelectionEnabled: boolean;
  /** The provider has not settled, so the flag reads as its default and must not be acted on. */
  isAwaitingFlag: boolean;
  /**
   * What `useHydrateWorkspaceId` takes: defer the alphabetical default while
   * the flag is unsettled OR on. Flips to `false` when the provider settles OFF,
   * and hydrate's effect then auto-picks exactly as it did before the selector.
   */
  shouldDeferInitialPick: boolean;
};

/**
 * Active workspace id for OpenFeature trait sync (`workspace_id` alongside
 * `org_id`). Re-exported here so `@/features/flags` modules may bind workspace
 * identity through `./flags` only — `domain-flag-doors.test.ts` refuses `/data`.
 */
export { useWorkspaceId } from './data/query/useWorkspaceId';
